/**
 * Utilities for extracting filenames from uploaded files, including zip archives
 */
import JSZip from 'jszip';

/**
 * Extract all filenames from a file, recursively reading zip contents
 * @param file The file to extract filenames from
 * @returns Array of filenames found in the file (or zip archive)
 */
export async function extractFilenames(file: File): Promise<string[]> {
  // For zip files, extract the directory listing
  if (file.name.toLowerCase().endsWith('.zip')) {
    try {
      const zip = new JSZip();
      const zipContents = await zip.loadAsync(file);

      // Get all filenames in the zip, excluding directories
      const filenames = Object.keys(zipContents.files)
        .filter(name => !zipContents.files[name].dir)
        .filter(name => !name.endsWith('/')); // Extra safety for directories

      return filenames;
    } catch (error) {
      console.error('Failed to read zip file:', error);
      // Fall back to just the zip filename
      return [file.name];
    }
  }

  // For non-zip files, just return the filename
  return [file.name];
}

/**
 * Extract filenames from multiple files
 * @param files Array of files to extract filenames from
 * @returns Combined array of all filenames
 */
export async function extractFilenamesFromMultiple(files: (File | null)[]): Promise<string[]> {
  const allFilenames: string[] = [];

  for (const file of files) {
    if (file) {
      const filenames = await extractFilenames(file);
      allFilenames.push(...filenames);
    }
  }

  return allFilenames;
}
